import React, { useState } from 'react';
import api from '../api/axios';
import { UserPlus, Mail, Lock, RefreshCw, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/api/admin/register', { email, password });
      setSuccess(`Administrator ${email} has been successfully registered.`);
      setEmail('');
      setPassword('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to register new administrator. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto space-y-6">
      <div className="text-center">
        <div className="inline-flex p-3 bg-blue-100 rounded-full text-blue-600 mb-4">
          <UserPlus className="w-8 h-8" />
        </div>
        <h1 className="text-2xl font-bold text-gray-800">Add Administrator</h1>
        <p className="text-gray-500 text-sm">Create a new account with full administrative privileges.</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-3 shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center">
          <CheckCircle className="w-5 h-5 mr-3 shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-700">Email Address</label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all"
                placeholder="admin@example.com"
                required
              />
              <Mail className="absolute left-3.5 top-3 text-gray-400 w-5 h-5" />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-700">Initial Password</label>
            <div className="relative group">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-11 pr-12 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all"
                placeholder="••••••••"
                required
                minLength={6}
              />
              <Lock className="absolute left-3.5 top-3 text-gray-400 group-focus-within:text-blue-500 transition-colors w-5 h-5" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-3 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center py-3 px-4 bg-gray-900 text-white font-bold rounded-lg hover:bg-gray-800 focus:ring-4 focus:ring-gray-200 disabled:opacity-50 transition-all shadow-lg"
            >
              {loading ? <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> : <UserPlus className="w-5 h-5 mr-2" />}
              {loading ? 'Processing...' : 'Create Admin Account'}
            </button>
          </div>
        </form>
      </div>
      
      <div className="text-center">
        <p className="text-xs text-gray-400">
          New administrators will be able to log in immediately after registration.
        </p>
      </div>
    </div>
  );
};

export default Register;
